// Operator login — same 2-step (password + TOTP) shape as
// web-backoffice. The cookie minted here covers /admin/mm/* on the
// gateway because the gateway applies admin auth uniformly across
// /admin/<anything>.
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APIError } from "@/lib/api";
import { login, completeMFA, fetchMeOrNull } from "@/lib/auth";
import { keys } from "@/lib/queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SparkHorizontal } from "@/components/ui/SparkHorizontal";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useI18n } from "@/i18n/I18nProvider";
import type { TranslationKey } from "@/i18n/en";
import type {
  AdminLoginResponse,
  AdminLoginMFAChallenge,
  AdminLoginMFAFactor,
  AdminMe,
} from "@/lib/types";

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (me) throw redirect({ to: search.redirect ?? "/" });
  },
  component: LoginPage,
});

type Step =
  | { kind: "password" }
  | { kind: "mfa"; challenge: AdminLoginMFAChallenge; factor: AdminLoginMFAFactor };

function humanError(e: unknown, step: "password" | "mfa", t: (key: TranslationKey) => string): string {
  if (e instanceof APIError) {
    if (e.status === 401) {
      return step === "mfa"
        ? t("login.error.codeRejected")
        : t("login.error.credentials");
    }
    if (e.status === 429) return t("login.error.rateLimit");
    if (e.status === 0 || e.status >= 500) return t("login.error.unreachable");
    if (e.message && !/^HTTP \d+$/.test(e.message)) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return step === "mfa" ? t("login.error.verify") : t("login.error.signIn");
}

function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (step.kind === "mfa") otpRefs.current[0]?.focus();
  }, [step.kind]);

  const goAfterAuth = (res: AdminLoginResponse) => {
    const me: AdminMe = {
      user_id: res.user_id,
      email: res.email,
      role: res.role,
      csrf_token: res.csrf_token,
      expires_at: res.expires_at,
    };
    qc.setQueryData(keys.me(), me);
    navigate({ to: search.redirect ?? "/" });
  };

  const passwordMut = useMutation({
    mutationFn: () => login({ email, password }),
    onSuccess: (res) => {
      setErr(null);
      if (res.kind === "needs_mfa") {
        const factor = res.challenge.factors[0];
        if (!factor) {
          setErr(t("login.error.noFactor"));
          return;
        }
        setStep({ kind: "mfa", challenge: res.challenge, factor });
        return;
      }
      goAfterAuth(res.data);
    },
    onError: (e) => setErr(humanError(e, "password", t)),
  });

  const mfaMut = useMutation({
    mutationFn: () => {
      if (step.kind !== "mfa") throw new Error("not in MFA step");
      return completeMFA({
        challenge_id: step.challenge.challenge_id,
        factor_id: step.factor.factor_id,
        code: otp.join(""),
      });
    },
    onSuccess: (res) => {
      setErr(null);
      goAfterAuth(res);
    },
    onError: (e) => setErr(humanError(e, "mfa", t)),
  });

  function setOtpAt(i: number, value: string) {
    const digits = value.replace(/\D/g, "");
    if (!digits) {
      const next = otp.slice();
      next[i] = "";
      setOtp(next);
      return;
    }
    const next = otp.slice();
    let cursor = i;
    for (const d of digits) {
      if (cursor > 5) break;
      next[cursor] = d;
      cursor++;
    }
    setOtp(next);
    otpRefs.current[Math.min(cursor, 5)]?.focus();
    if (cursor >= 6 && next.every((d) => d !== "")) mfaMut.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <SparkHorizontal viewBox="243 632 1611 628" className="h-8 w-auto shrink-0 text-t-1" />
          <div className="mr-auto text-2xs uppercase tracking-[0.18em] text-t-3">{t("login.brand")}</div>
          <LanguageSelector compact />
        </div>
        <div className="modal">
          <div className="modal-glow" />
          <div className="modal-body">
            {step.kind === "password" ? (
              <>
                <div className="auth-eyebrow">{t("login.operatorConsole")}</div>
                <h2 className="auth-headline">{t("login.signIn")}</h2>
                <p className="auth-sub">{t("login.subtitle")}</p>
                {err && (
                  <div className="banner danger" role="alert">
                    <span aria-hidden>⚠</span>
                    <span>{err}</span>
                  </div>
                )}
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    passwordMut.mutate();
                  }}
                >
                  <Input
                    label={t("login.email")}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                  <Input
                    label={t("login.password")}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <Button type="submit" loading={passwordMut.isPending} className="btn-block btn-lg mt-1">
                    {passwordMut.isPending ? t("login.signingIn") : t("login.continue")}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="auth-eyebrow">{t("login.twoFactor")}</div>
                <h2 className="auth-headline">{t("login.enterCode")}</h2>
                <p className="auth-sub">
                  {t("login.codeInstruction", { email: step.challenge.email })}
                </p>
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    mfaMut.mutate();
                  }}
                >
                  <div className="otp">
                    {otp.map((v, i) => (
                      <input
                        key={i}
                        ref={(el) => {
                          otpRefs.current[i] = el;
                        }}
                        maxLength={6}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={v}
                        onChange={(e) => setOtpAt(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !v && i > 0) otpRefs.current[i - 1]?.focus();
                        }}
                        autoComplete="one-time-code"
                        className={v ? "filled" : undefined}
                        aria-label={t("login.digitAria", { digit: i + 1 })}
                      />
                    ))}
                  </div>
                  {err && (
                    <div className="banner danger" role="alert">
                      <span aria-hidden>⚠</span>
                      <span>{err}</span>
                    </div>
                  )}
                  <Button type="submit" loading={mfaMut.isPending} disabled={otp.some((d) => !d)} className="btn-block btn-lg">
                    {mfaMut.isPending ? t("login.verifying") : t("login.verify")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep({ kind: "password" });
                      setOtp(["", "", "", "", "", ""]);
                      setErr(null);
                    }}
                    className="block w-full text-center text-2xs text-t-3 transition-colors hover:text-t-1"
                  >
                    {t("login.back")}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
